import { canonicalLoomDefinitionJson } from "../features/parametric-geometry/loomAcceptedDefinitionBinding";

export interface LoomV3SourceBinding {
  adapter: "openvsp-vsp3";
  sourceAssetSha256: `sha256:${string}`;
  sourceAssetId: string;
  selectedComponents: [
    { componentId: string; role: "wing" },
    { componentId: string; role: "fuselage" },
  ];
  normalization: {
    policyId: string;
    sourceLengthUnit: "unbound";
    sourceBodyLengthNative: number;
    targetBodyLengthM: number;
    metresPerNativeUnit: number;
  };
}

export interface LoomV3SourceTrailingEdge {
  sharpFlag: boolean;
  closeType: 0 | 1 | 2 | 3 | 4;
  closeThicknessRatio: number;
}

export interface LoomV3Naca4Section {
  family: "naca4";
  camberRatio: number;
  camberLocationRatio: number;
  thicknessRatio: number;
  closedTrailingEdge: boolean;
  trailingEdgeThicknessRatio: number;
  sourceTrailingEdge: LoomV3SourceTrailingEdge;
}

export interface LoomV3CstSection {
  family: "cst";
  upperCoefficients: number[];
  lowerCoefficients: number[];
  trailingEdgeThicknessRatio: number;
  sourceTrailingEdge: LoomV3SourceTrailingEdge;
}

export interface LoomV3PolygonSection {
  family: "polygon";
  vertices: Array<[number, number]>;
  closedTrailingEdge: boolean;
  trailingEdgeThicknessRatio: number;
  sourceTrailingEdge: LoomV3SourceTrailingEdge;
}

export type LoomV3Section = LoomV3Naca4Section | LoomV3CstSection | LoomV3PolygonSection;

export type LoomV3Profile = {
  kind: "superellipse";
  widthM: number;
  heightM: number;
  widthExponent: number;
  heightExponent: number;
  zCenterM: number;
} | {
  kind: "polygon";
  verticesM: Array<[number, number]>;
};

export type LoomV3FuselageStation = {
  stationId: string;
  kind: "point";
  xM: number;
  zCenterM: number;
} | {
  stationId: string;
  kind: "profile";
  xM: number;
  profileRef: string;
};

export interface LoomWingBodyDefinitionV2 {
  schema: "loom.wing-body-definition/v2";
  sourceBinding: LoomV3SourceBinding;
  sectionDefinitions: Record<string, LoomV3Section>;
  profileDefinitions: Record<string, LoomV3Profile>;
  wing: {
    sourceNodeId: string;
    instanceNodeId: string;
    transformNodeIds: string[];
    tip: "flat";
    tipRadiusM: 0;
    panel: {
      panelId: string;
      yRootM: number;
      yTipM: number;
      chordRootM: number;
      chordTipM: number;
      sweepDeg: number;
      sweepLocationRatio: number;
      dihedralDeg: number;
      twistRootDeg: number;
      twistTipDeg: number;
      twistLocationRatio: number;
      xOffsetM: number;
      zOffsetM: number;
      airfoilRootRef: string;
      airfoilTipRef: string;
      sectionSamples: number;
    };
    placement: {
      kind: "rigid";
      offsetM: [number, number, number];
      rotationDegXYZ: [number, number, number];
    };
  };
  fuselage: {
    sourceNodeId: string;
    stations: LoomV3FuselageStation[];
    sourceEndCaps: { noseOption: 0 | 1; tailOption: 0 | 1 };
    endCaps: { nose: "point" | "flat"; tail: "point" | "flat" };
  };
}

const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/;
const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/;
const MAX_LENGTH = 1_000_000;

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function exact(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  const item = record(value, label); const actual = Object.keys(item);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label} has an unsupported field set`);
  }
  return item;
}

function text(value: unknown, label: string, pattern = TOKEN): string {
  if (typeof value !== "string" || !pattern.test(value)) throw new Error(`${label} is invalid`);
  return value;
}

function finite(
  value: unknown, label: string, minimum = -Infinity, maximum = Infinity,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)
      || value < minimum || value > maximum) {
    throw new Error(`${label} must be a bounded finite number`);
  }
  return value;
}

function positive(value: unknown, label: string, maximum = MAX_LENGTH): number {
  const result = finite(value, label, 0, maximum);
  if (result === 0) throw new Error(`${label} must be positive`);
  return result;
}

function integer(value: unknown, label: string, minimum: number, maximum: number): number {
  const result = finite(value, label, minimum, maximum);
  if (!Number.isSafeInteger(result)) throw new Error(`${label} must be an integer`);
  return result;
}

function polygon(value: unknown, label: string, maximum: number): Array<[number, number]> {
  if (!Array.isArray(value) || value.length < 3 || value.length > maximum) {
    throw new Error(`${label} exceeds its point budget`);
  }
  const points = value.map((point, index) => {
    if (!Array.isArray(point) || point.length !== 2) throw new Error(`${label}[${index}] is malformed`);
    return [
      finite(point[0], `${label}[${index}][0]`, -MAX_LENGTH, MAX_LENGTH),
      finite(point[1], `${label}[${index}][1]`, -MAX_LENGTH, MAX_LENGTH),
    ] as [number, number];
  });
  const area2 = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
  }, 0);
  if (Math.abs(area2) <= 1e-14) throw new Error(`${label} is degenerate`);
  return points;
}

function sourceTrailingEdge(value: unknown, label: string): LoomV3SourceTrailingEdge {
  const item = exact(value, ["sharpFlag", "closeType", "closeThicknessRatio"], label);
  if (typeof item.sharpFlag !== "boolean") throw new Error(`${label}.sharpFlag must be boolean`);
  const closeType = integer(item.closeType, `${label}.closeType`, 0, 4);
  const closeThicknessRatio = finite(
    item.closeThicknessRatio, `${label}.closeThicknessRatio`, 0, 1,
  );
  if ((item.sharpFlag && (closeType !== 0 || closeThicknessRatio !== 0))
      || (closeType === 0 && closeThicknessRatio !== 0)) {
    throw new Error(`${label} is internally contradictory`);
  }
  return {
    sharpFlag: item.sharpFlag,
    closeType: closeType as LoomV3SourceTrailingEdge["closeType"],
    closeThicknessRatio,
  };
}

function polygonTrailingEdgeGap(points: Array<[number, number]>, sharp: boolean): number {
  const area2 = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return sum + point[0] * next[1] - next[0] * point[1];
  }, 0);
  const ordered = area2 < 0 ? [...points].reverse() : [...points];
  const maximumX = Math.max(...ordered.map(([x]) => x));
  const trailing = ordered.map((point, index) => ({ point, index }))
    .filter(({ point }) => Math.abs(point[0] - maximumX) <= 1e-10);
  if (sharp) {
    const spread = Math.max(...trailing.flatMap(({ point: first }) => (
      trailing.flatMap(({ point: second }) => (
        [Math.abs(first[0] - second[0]), Math.abs(first[1] - second[1])]
      ))
    )), -Infinity);
    if (spread > 1e-13) throw new Error("sharp polygon has a finite trailing-edge gap");
    return 0;
  }
  if (trailing.length < 2) throw new Error("polygon does not expose a trailing-edge pair");
  const start = trailing.reduce((best, candidate) => (
    candidate.point[1] > best.point[1] ? candidate : best
  )).index;
  const first = ordered[start]!;
  const last = ordered[(start + ordered.length - 1) % ordered.length]!;
  return Math.hypot(first[0] - last[0], first[1] - last[1]);
}

function validateTrailingEdge(
  source: LoomV3SourceTrailingEdge, family: LoomV3Section["family"],
  closed: boolean, gap: number, points?: Array<[number, number]>,
): void {
  let coherent = source.sharpFlag
    ? closed && gap === 0
    : source.closeType === 0
      ? family === "naca4" && !closed && gap === 0
      : closed && Math.abs(gap - source.closeThicknessRatio) <= 1e-14;
  if (coherent && points) {
    try {
      const actual = polygonTrailingEdgeGap(points, source.sharpFlag);
      coherent = Math.abs(actual - gap) <= Math.max(2e-8, 2e-5 * Math.abs(gap));
    } catch {
      coherent = false;
    }
  }
  if (!coherent) throw new Error("target trailing edge differs from its bound source state");
}

function section(value: unknown, label: string): void {
  const item = record(value, label);
  const gap = finite(item.trailingEdgeThicknessRatio, `${label}.trailingEdgeThicknessRatio`, 0, 1);
  const source = sourceTrailingEdge(item.sourceTrailingEdge, `${label}.sourceTrailingEdge`);
  if (item.family === "naca4") {
    exact(item, ["family", "camberRatio", "camberLocationRatio", "thicknessRatio", "closedTrailingEdge", "trailingEdgeThicknessRatio", "sourceTrailingEdge"], label);
    const camber = finite(item.camberRatio, `${label}.camberRatio`, 0, 1);
    const location = finite(item.camberLocationRatio, `${label}.camberLocationRatio`, 0, 1);
    positive(item.thicknessRatio, `${label}.thicknessRatio`, 1);
    if (typeof item.closedTrailingEdge !== "boolean") throw new Error(`${label}.closedTrailingEdge is invalid`);
    if (camber > 0 && !(location > 0 && location < 1)) {
      throw new Error("nonzero NACA4 camber requires an interior camber location");
    }
    validateTrailingEdge(source, "naca4", item.closedTrailingEdge, gap);
    return;
  }
  if (item.family === "cst") {
    exact(item, ["family", "upperCoefficients", "lowerCoefficients", "trailingEdgeThicknessRatio", "sourceTrailingEdge"], label);
    const upper = item.upperCoefficients; const lower = item.lowerCoefficients;
    if (!Array.isArray(upper) || !Array.isArray(lower) || upper.length < 1
        || upper.length > 32 || upper.length !== lower.length) {
      throw new Error(`${label} coefficient arrays are invalid`);
    }
    upper.forEach((entry, index) => finite(
      entry, `${label}.upperCoefficients[${index}]`, -MAX_LENGTH, MAX_LENGTH));
    lower.forEach((entry, index) => finite(
      entry, `${label}.lowerCoefficients[${index}]`, -MAX_LENGTH, MAX_LENGTH));
    validateTrailingEdge(source, "cst", true, gap);
    return;
  }
  if (item.family === "polygon") {
    exact(item, ["family", "vertices", "closedTrailingEdge", "trailingEdgeThicknessRatio", "sourceTrailingEdge"], label);
    if (typeof item.closedTrailingEdge !== "boolean") throw new Error(`${label}.closedTrailingEdge is invalid`);
    const points = polygon(item.vertices, `${label}.vertices`, 401);
    validateTrailingEdge(source, "polygon", item.closedTrailingEdge, gap, points);
    return;
  }
  throw new Error(`${label}.family is unsupported`);
}

function profile(value: unknown, label: string): void {
  const item = record(value, label);
  if (item.kind === "superellipse") {
    exact(item, ["kind", "widthM", "heightM", "widthExponent", "heightExponent", "zCenterM"], label);
    positive(item.widthM, `${label}.widthM`); positive(item.heightM, `${label}.heightM`);
    positive(item.widthExponent, `${label}.widthExponent`, 100);
    positive(item.heightExponent, `${label}.heightExponent`, 100);
    finite(item.zCenterM, `${label}.zCenterM`, -MAX_LENGTH, MAX_LENGTH); return;
  }
  if (item.kind === "polygon") {
    exact(item, ["kind", "verticesM"], label); polygon(item.verticesM, `${label}.verticesM`, 401); return;
  }
  throw new Error(`${label}.kind is unsupported`);
}

function table(
  value: unknown, label: string, maximum: number,
  parser: (entry: unknown, path: string) => void,
): Set<string> {
  const item = record(value, label); const entries = Object.entries(item);
  if (entries.length < 1 || entries.length > maximum) throw new Error(`${label} exceeds its value budget`);
  const ids = new Set<string>(); const signatures = new Set<string>();
  entries.forEach(([key, entry]) => {
    text(key, `${label} ID`); parser(entry, `${label}.${key}`);
    const signature = canonicalLoomDefinitionJson(entry);
    if (signatures.has(signature)) throw new Error(`${label} contains a duplicate canonical value`);
    signatures.add(signature); ids.add(key);
  });
  return ids;
}

function sameSet(left: Set<string>, right: Set<string>): boolean {
  return left.size === right.size && [...left].every((value) => right.has(value));
}

export function parseLoomWingBodyDefinitionV2(value: unknown): LoomWingBodyDefinitionV2 {
  if (new TextEncoder().encode(canonicalLoomDefinitionJson(value)).byteLength > 262_144) {
    throw new Error("v3 definition exceeds 256 KiB");
  }
  const root = exact(value, ["schema", "sourceBinding", "sectionDefinitions", "profileDefinitions", "wing", "fuselage"], "v3 definition");
  if (root.schema !== "loom.wing-body-definition/v2") throw new Error("v3 definition schema is unsupported");
  const source = exact(root.sourceBinding, ["adapter", "sourceAssetId", "sourceAssetSha256", "selectedComponents", "normalization"], "v3 source binding");
  if (source.adapter !== "openvsp-vsp3" || typeof source.sourceAssetSha256 !== "string"
      || !/^sha256:[0-9a-f]{64}$/.test(source.sourceAssetSha256)) throw new Error("v3 source binding is malformed");
  text(source.sourceAssetId, "sourceAssetId");
  const selectedComponents = source.selectedComponents;
  if (!Array.isArray(selectedComponents) || selectedComponents.length !== 2) throw new Error("v3 selected components are malformed");
  const componentIds = (["wing", "fuselage"] as const).map((role, index) => {
    const item = exact(selectedComponents[index], ["componentId", "role"], `selectedComponents[${index}]`);
    if (item.role !== role) throw new Error("v3 selected components must be ordered wing then fuselage");
    return text(item.componentId, "componentId");
  });
  if (componentIds[0] === componentIds[1]) throw new Error("v3 selected component IDs collide");
  const normalization = exact(source.normalization, ["policyId", "sourceLengthUnit", "sourceBodyLengthNative", "targetBodyLengthM", "metresPerNativeUnit"], "v3 normalization");
  text(normalization.policyId, "policyId", IDENTIFIER);
  if (normalization.sourceLengthUnit !== "unbound") throw new Error("v3 source length unit must remain unbound");
  const sourceLength = positive(normalization.sourceBodyLengthNative, "sourceBodyLengthNative");
  const targetLength = positive(normalization.targetBodyLengthM, "targetBodyLengthM");
  const scale = positive(normalization.metresPerNativeUnit, "metresPerNativeUnit");
  if (Math.abs(targetLength / sourceLength - scale) > Math.max(1e-15, Math.abs(scale) * 1e-12)) throw new Error("v3 normalization ratio is inconsistent");
  const sectionIds = table(root.sectionDefinitions, "sectionDefinitions", 2, section);
  const profileIds = table(root.profileDefinitions, "profileDefinitions", 257, profile);
  const wing = exact(root.wing, ["sourceNodeId", "instanceNodeId", "transformNodeIds", "tip", "tipRadiusM", "panel", "placement"], "v3 wing");
  text(wing.sourceNodeId, "wing.sourceNodeId"); text(wing.instanceNodeId, "wing.instanceNodeId");
  if (wing.tip !== "flat" || finite(wing.tipRadiusM, "tipRadiusM") !== 0
      || !Array.isArray(wing.transformNodeIds) || wing.transformNodeIds.length > 64) throw new Error("v3 wing provenance or tip is unsupported");
  const transforms = wing.transformNodeIds.map((item, index) => text(item, `transformNodeIds[${index}]`));
  if (new Set(transforms).size !== transforms.length) throw new Error("v3 transform IDs collide");
  const panel = exact(wing.panel, ["panelId", "yRootM", "yTipM", "chordRootM", "chordTipM", "sweepDeg", "sweepLocationRatio", "dihedralDeg", "twistRootDeg", "twistTipDeg", "twistLocationRatio", "xOffsetM", "zOffsetM", "airfoilRootRef", "airfoilTipRef", "sectionSamples"], "v3 panel");
  text(panel.panelId, "panelId"); const yRoot = finite(panel.yRootM, "yRootM", -MAX_LENGTH, MAX_LENGTH); const yTip = finite(panel.yTipM, "yTipM", -MAX_LENGTH, MAX_LENGTH);
  positive(panel.chordRootM, "chordRootM"); positive(panel.chordTipM, "chordTipM"); const sweep = finite(panel.sweepDeg, "sweepDeg"); const dihedral = finite(panel.dihedralDeg, "dihedralDeg");
  finite(panel.sweepLocationRatio, "sweepLocationRatio", 0, 1); finite(panel.twistRootDeg, "twistRootDeg"); finite(panel.twistTipDeg, "twistTipDeg"); finite(panel.twistLocationRatio, "twistLocationRatio", 0, 1); finite(panel.xOffsetM, "xOffsetM", -MAX_LENGTH, MAX_LENGTH); finite(panel.zOffsetM, "zOffsetM", -MAX_LENGTH, MAX_LENGTH);
  const rootRef = text(panel.airfoilRootRef, "airfoilRootRef"); const tipRef = text(panel.airfoilTipRef, "airfoilTipRef"); const samples = integer(panel.sectionSamples, "sectionSamples", 11, 101);
  if (yTip <= yRoot || Math.abs(sweep) >= 89 || Math.abs(dihedral) >= 89 || samples % 2 === 0) throw new Error("v3 panel is degenerate");
  const placement = exact(wing.placement, ["kind", "offsetM", "rotationDegXYZ"], "v3 placement");
  if (placement.kind !== "rigid" || !Array.isArray(placement.offsetM) || placement.offsetM.length !== 3 || !Array.isArray(placement.rotationDegXYZ) || placement.rotationDegXYZ.length !== 3) throw new Error("v3 placement is unsupported");
  placement.offsetM.forEach((item, index) => finite(item, `offsetM[${index}]`, -MAX_LENGTH, MAX_LENGTH)); placement.rotationDegXYZ.forEach((item, index) => finite(item, `rotationDegXYZ[${index}]`, -MAX_LENGTH, MAX_LENGTH));
  const fuselage = exact(root.fuselage, ["sourceNodeId", "stations", "sourceEndCaps", "endCaps"], "v3 fuselage"); text(fuselage.sourceNodeId, "fuselage.sourceNodeId");
  if (!Array.isArray(fuselage.stations) || fuselage.stations.length < 2 || fuselage.stations.length > 257) throw new Error("v3 station count is invalid");
  const stationIds = new Set<string>(); const usedProfiles = new Set<string>(); let previousX = -Infinity;
  const stationKinds = fuselage.stations.map((entry, index) => {
    const station = record(entry, `stations[${index}]`); const id = text(station.stationId, "stationId");
    if (stationIds.has(id)) throw new Error("v3 station IDs collide"); stationIds.add(id);
    const x = finite(station.xM, "xM", -MAX_LENGTH, MAX_LENGTH); if (x <= previousX) throw new Error("v3 stations are not ordered"); previousX = x;
    if (station.kind === "point") { exact(station, ["stationId", "kind", "xM", "zCenterM"], `stations[${index}]`); finite(station.zCenterM, "zCenterM", -MAX_LENGTH, MAX_LENGTH); return "point"; }
    if (station.kind === "profile") { exact(station, ["stationId", "kind", "xM", "profileRef"], `stations[${index}]`); usedProfiles.add(text(station.profileRef, "profileRef")); return "profile"; }
    throw new Error("v3 station kind is unsupported");
  });
  const sourceCaps = exact(fuselage.sourceEndCaps, ["noseOption", "tailOption"], "v3 source end caps"); const noseOption = integer(sourceCaps.noseOption, "noseOption", 0, 1); const tailOption = integer(sourceCaps.tailOption, "tailOption", 0, 1);
  const caps = exact(fuselage.endCaps, ["nose", "tail"], "v3 end caps"); const expectedNose = stationKinds[0] === "point" ? "point" : "flat"; const expectedTail = stationKinds[stationKinds.length - 1] === "point" ? "point" : "flat";
  if (caps.nose !== expectedNose || caps.tail !== expectedTail || expectedNose === "point" && noseOption === 1 || expectedTail === "point" && tailOption === 1) throw new Error("v3 source endpoint and cap policy disagree");
  if (!sameSet(sectionIds, new Set([rootRef, tipRef])) || !sameSet(profileIds, usedProfiles)) throw new Error("v3 definition tables are not exactly referenced");
  const sections = root.sectionDefinitions as Record<string, LoomV3Section>;
  if (sections[rootRef]!.sourceTrailingEdge.sharpFlag
      !== sections[tipRef]!.sourceTrailingEdge.sharpFlag) {
    throw new Error("v3 root/tip source trailing-edge sharpness mismatch is unsupported");
  }
  return value as LoomWingBodyDefinitionV2;
}

export function parseLoomAuthoritativeRebuildRequestV3(value: unknown): void {
  const encoded = new TextEncoder().encode(canonicalLoomDefinitionJson(value));
  if (encoded.byteLength > 262_144 + 4_096) {
    throw new Error("v3 rebuild request exceeds its serialized budget");
  }
  const root = exact(value, ["schema", "requestId", "sourceDefinition", "evaluatorId", "topology", "definition"], "v3 rebuild request");
  if (root.schema !== "loom.authoritative-rebuild-request/v3"
      || root.evaluatorId !== "loom.native-wing-body/v3"
      || root.topology !== "selected-wing-fuselage-pairwise-union") {
    throw new Error("v3 rebuild request discriminator is unsupported");
  }
  text(root.requestId, "requestId");
  const source = exact(root.sourceDefinition, ["documentId", "revision", "stateId"], "sourceDefinition");
  text(source.documentId, "sourceDefinition.documentId");
  integer(source.revision, "sourceDefinition.revision", 0, Number.MAX_SAFE_INTEGER);
  text(source.stateId, "sourceDefinition.stateId");
  parseLoomWingBodyDefinitionV2(root.definition);
}
